import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { rivattiLines } from '@/mocks/rivatti';
import { useSEO } from '@/hooks/useSEO';

export default function RivattiPage() {
  const [activeTab, setActiveTab] = useState('lancamentos');

  useSEO({
    title: 'Rivatti Móveis Caruaru PE | Revendedor Autorizado Restaurante e Food Service',
    description: 'Revendedor autorizado Rivatti Móveis em Caruaru PE. Cadeiras e mesas para restaurante, food service, outdoor e infantil. Entrega e montagem em Caruaru e todo o Agreste PE.',
    keywords: 'Rivatti móveis Recife, revendedor Rivatti Petrolina, móveis restaurante Garanhuns, cadeiras food service Arcoverde, móveis outdoor Sertão PE, Rivatti Pernambuco, cadeiras restaurante Agreste, Rivatti Nordeste, mesas restaurante Rivatti Recife, cadeiras empilháveis Rivatti Petrolina, móveis lanchonete Garanhuns, Rivatti Caruaru, Rivatti Bezerros, cadeiras Rivatti Gravatá',
    path: '/rivatti',
    ogTitle: 'Rivatti Móveis Caruaru PE | Revendedor Autorizado – Home Office Premium',
    ogDescription: 'Revendedor autorizado Rivatti em Caruaru PE. Móveis para restaurante, food service, outdoor e infantil com entrega e montagem.',
  });

  useEffect(() => {
    const schemaId = 'schema-rivatti';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Rivatti Móveis Caruaru PE – Home Office Premium',
      description: 'Revendedor autorizado Rivatti em Caruaru PE. Cadeiras e mesas para restaurante, food service, linha outdoor, infantil e decoração.',
      url: `${import.meta.env.VITE_SITE_URL ?? ''}/rivatti`,
      about: {
        '@type': 'Brand',
        name: 'Rivatti',
        description: 'Uma das maiores fabricantes de móveis para food service do Brasil. Cadeiras para restaurantes, linha outdoor, infantil e decoração.',
      },
    });

    const breadcrumbId = 'schema-rivatti-breadcrumb';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? '' },
        { '@type': 'ListItem', position: 2, name: 'Marcas', item: `${import.meta.env.VITE_SITE_URL ?? ''}/marcas` },
        { '@type': 'ListItem', position: 3, name: 'Rivatti', item: `${import.meta.env.VITE_SITE_URL ?? ''}/rivatti` },
      ],
    });
    return () => { script?.remove(); bcScript?.remove(); };
  }, []);

  const activeLine = rivattiLines.find(l => l.id === activeTab);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 h-[520px] md:h-[600px] overflow-hidden">
        <img
          src="https://www.rivatti.com.br/_files/subcategoria/freepik__ambientar-essa-mesa-com-essas-cadeiras-numa-sala-d__556521.jpeg"
          alt="Rivatti Móveis"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
                <i className="ri-star-fill text-xs"></i>
                Marca Parceira Oficial
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                Rivatti<br />
                <span className="text-[#E8610A]">Móveis</span>
              </h1>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
                Uma das maiores fabricantes de móveis para food service e decoração do Brasil. Do restaurante à área externa, a Rivatti tem a solução certa para cada ambiente.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.rivatti.com.br/produtos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 text-sm font-semibold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  Ver Catálogo Completo
                </a>
                <a
                  href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#C4500A] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  Solicitar Orçamento
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sobre */}
      <section className="py-20 bg-[#fafaf8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Quem é a Rivatti</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Referência nacional em mobiliário para food service
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                A Rivatti é uma das marcas mais reconhecidas do Brasil no segmento de móveis para restaurantes, lanchonetes, praças de alimentação e ambientes externos. Com décadas de experiência, a marca une design, resistência e custo-benefício.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                Somos revendedores autorizados da Rivatti em Caruaru e região, oferecendo toda a linha de produtos com entrega, montagem e suporte técnico especializado.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'ri-shield-check-line', label: 'Garantia de Fábrica' },
                  { icon: 'ri-truck-line', label: 'Entrega em todo Brasil' },
                  { icon: 'ri-tools-line', label: 'Suporte Técnico' },
                  { icon: 'ri-award-line', label: 'Revendedor Autorizado' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#E8610A]/10 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#E8610A] text-sm`}></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '8+', label: 'Linhas de Produtos' },
                { value: '200+', label: 'Modelos no Catálogo' },
                { value: '100%', label: 'Garantia de Fábrica' },
                { value: 'BR', label: 'Fabricação Nacional' },
              ].map(stat => (
                <div key={stat.label} className="bg-white rounded-2xl p-6 text-center border border-gray-100">
                  <p className="text-3xl font-bold text-[#E8610A] mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Linhas */}
      <section id="linhas" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Catálogo</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Linhas Rivatti</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              Selecione uma linha para ver os produtos. Clique em qualquer produto para ver detalhes no site oficial.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {rivattiLines.map(line => (
              <button
                key={line.id}
                onClick={() => setActiveTab(line.id)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === line.id
                    ? 'bg-[#E8610A] text-white'
                    : 'bg-[#f5f5f0] text-gray-600 hover:bg-[#ece9e4]'
                }`}
              >
                <i className={`${line.icon} text-sm`}></i>
                {line.label}
              </button>
            ))}
          </div>

          {/* Conteúdo da linha ativa */}
          {activeLine && (
            <>
              {/* Banner da linha */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12">
                <div className="rounded-2xl overflow-hidden h-96 bg-[#f8f8f6]">
                  <img
                    src={activeLine.image}
                    alt={activeLine.label}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Linha {activeLine.label}</p>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{activeLine.description}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-8">
                    Trabalhamos com toda a linha <strong>{activeLine.label}</strong> da Rivatti. Acesse o catálogo oficial para ver todos os modelos, cores e especificações técnicas. Depois é só entrar em contato com a gente para orçamento.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={activeLine.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#C4500A] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-external-link-line"></i>
                      Ver linha no site Rivatti
                    </a>
                    <a
                      href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#f5f5f0] text-gray-700 text-sm font-medium px-6 py-3 rounded-full hover:bg-[#ece9e4] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line"></i>
                      Pedir orçamento
                    </a>
                  </div>
                </div>
              </div>

              {/* Grid de produtos */}
              {activeLine.products.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-gray-900 font-bold text-base">Produtos desta linha</h4>
                    <a
                      href={activeLine.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#E8610A] text-sm font-medium hover:underline whitespace-nowrap cursor-pointer"
                    >
                      Ver todos <i className="ri-external-link-line text-xs"></i>
                    </a>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                    {activeLine.products.map(product => (
                      <a
                        key={product.name}
                        href={product.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-[#E8610A]/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                      >
                        <div className="h-56 bg-[#f8f8f6] overflow-hidden flex items-center justify-center p-3">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4">
                          <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-wider mb-1">Rivatti</p>
                          <h3 className="text-gray-900 font-bold text-sm leading-snug mb-3">{product.name}</h3>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400">Ver no site oficial</span>
                            <span className="w-7 h-7 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-full group-hover:bg-[#E8610A] group-hover:text-white transition-colors duration-200">
                              <i className="ri-external-link-line text-xs"></i>
                            </span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {/* Como funciona */}
      <section className="py-16 bg-[#fafaf8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-8">
            <p className="text-gray-900 font-bold text-base mb-6 text-center">Como funciona?</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { step: '01', icon: 'ri-search-eye-line', title: 'Acesse o catálogo', desc: 'Navegue pelo site oficial da Rivatti e escolha os produtos que te interessam.' },
                { step: '02', icon: 'ri-whatsapp-line', title: 'Fale com a gente', desc: 'Mande o nome ou foto do produto pelo WhatsApp. Nossa equipe cuida do resto.' },
                { step: '03', icon: 'ri-truck-line', title: 'Receba em casa', desc: 'Cuidamos da compra, entrega e montagem. Você só aproveita o resultado.' },
              ].map(item => (
                <div key={item.step} className="flex flex-col items-center text-center">
                  <div className="relative mb-4">
                    <div className="w-14 h-14 flex items-center justify-center bg-[#E8610A]/10 rounded-2xl">
                      <i className={`${item.icon} text-[#E8610A] text-2xl`}></i>
                    </div>
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#E8610A] text-white text-xs font-bold rounded-full">{item.step}</span>
                  </div>
                  <h4 className="text-gray-900 font-bold text-sm mb-2">{item.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-4">Revendedor Autorizado</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Quer montar ou renovar seu espaço<br className="hidden md:block" /> com produtos Rivatti?
          </h2>
          <p className="text-gray-400 text-sm mb-10 max-w-lg mx-auto">
            Fale com nossa equipe e receba um orçamento personalizado. Atendemos restaurantes, lanchonetes, escolas, empresas e residências.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-[#C4500A] transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-whatsapp-line"></i>
              Falar no WhatsApp
            </a>
            <a href="https://www.rivatti.com.br/produtos" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-external-link-line"></i>
              Ver Catálogo Oficial
            </a>
            <Link to="/contato"
              className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-mail-line"></i>
              Enviar Mensagem
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
